import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, User, Eye, EyeOff, Loader2, AlertCircle, ShieldAlert, AlertTriangle } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // State
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // [SECURITY INTEGRATION] New State for specific errors
  const [error, setError] = useState("");
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem("tau_remember_user");
    if (savedUser) {
      setUsername(savedUser);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLocked(false);
    setLoading(true);

    try {
      await login(username, password);
      
      if (rememberMe) {
        localStorage.setItem("tau_remember_user", username);
      } else {
        localStorage.removeItem("tau_remember_user");
      }

      // Force navigation to dashboard after successful login
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
      
    } catch (err) {
      console.error(err);
      
      // [SECURITY INTEGRATION] Handle Backend Error Codes
      if (err.response) {
        if (err.response.status === 429) {
          // BRUTE FORCE LOCKOUT
          setIsLocked(true);
          setError("Access Locked: Too many failed attempts. Please wait 15 minutes.");
        } else if (err.response.status === 401) {
          // INVALID CREDENTIALS OR IP BLOCK
          setError("Access Denied: Invalid credentials or unauthorized location.");
        } else {
          setError("Connection failed. Please check the server.");
        }
      } else {
        setError("Network error. Ensure the Lab Bridge is open.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4 relative overflow-hidden">
      
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-64 bg-emerald-600 transform -skew-y-3 origin-top-left z-0" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-emerald-100 rounded-full blur-3xl opacity-50 z-0" />

      <Card className="w-full max-w-md shadow-2xl border-0 z-10 overflow-hidden">
        
        {/* LOGO SECTION */}
        <div className="bg-white pt-8 pb-6 px-8 flex flex-col items-center text-center">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm border border-slate-100 p-1">
              <img 
               src="/tau-logo.png" 
               alt="TAU Logo" 
               className="w-full h-full object-contain"
              />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900 tracking-tight">
            TAU Hotspot
          </CardTitle>
          <CardDescription className="text-slate-500 mt-2 font-medium">
            Hotspot User Manager
          </CardDescription>
        </div>

        <CardContent className="px-8 pb-8 pt-0">
          
          {/* [SECURITY INTEGRATION] Enhanced Error Alert */}
          {error && (
            <Alert variant="destructive" className={`mb-6 ${isLocked ? "bg-red-50 border-red-200 text-red-800" : "bg-orange-50 border-orange-200 text-orange-800"}`}>
              {isLocked ? <ShieldAlert className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              <AlertDescription className="ml-2 font-semibold">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700 font-semibold">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter username"
                  className="pl-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={loading || isLocked} // Disable input if locked
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-slate-700 font-semibold">Password</Label>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading || isLocked} // Disable input if locked
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                  disabled={loading || isLocked}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="remember" 
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked)}
                className="border-slate-300 text-emerald-600 focus:ring-emerald-600"
                disabled={loading || isLocked}
              />
              <Label htmlFor="remember" className="text-sm font-medium text-slate-600 cursor-pointer select-none">
                Remember username
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5"
              disabled={loading || isLocked}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Authenticating...
                </>
              ) : (
                "Sign In to Manager"
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="bg-slate-50 border-t border-slate-100 py-4 flex flex-col justify-center text-xs text-slate-400 text-center gap-1">
          <div className="flex items-center gap-1.5 justify-center">
            <Lock className="w-3 h-3" />
            <span>Secure Access • Authorized Personnel Only</span>
          </div>
          <span>&copy; {new Date().getFullYear()} T2G Network Solutions</span>
        </CardFooter>
      </Card>
    </div>
  );
}